import { Box, Checkbox, Container, FormControlLabel } from "@mui/material";
import { IconBuildingBank, IconId } from "@tabler/icons-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import Text from "../../components/ui/Text";
import { ROUTES } from "../../constants/routes";

const PayoutGettingStarted = () => {
  const navigate = useNavigate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleBack = () => {
    navigate(ROUTES.SETUP_PAYOUT);
  };

  const handleTermsChange = (event) => {
    setAgreedToTerms(event.target.checked);
  };

  // Generate Stripe OAuth URL with error handling
  const getStripeOAuthUrl = () => {
    const clientId = process.env.REACT_APP_STRIPE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_STRIPE_REDIRECT_URI;

    if (!clientId || !redirectUri) {
      return "#"; // Return a placeholder URL to prevent errors
    }

    return `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&redirect_uri=${redirectUri}`;
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 2 }}>
      {/* Back Button */}
      <Box sx={{ mb: "24px" }}>
        <Button variant="back" onClick={handleBack} />
      </Box>

      {/* Page Header */}
      <Text variant="pageHeader" sx={{ mb: "16px", textAlign: "center" }}>
        Getting started
      </Text>

      {/* Description */}
      <Text
        variant="paragraph"
        component="p"
        sx={{
          mb: 2,
          textAlign: "center",
          color: "#000",
          fontSize: "16px",
        }}
      >
        You will be redirected to set up your merchant account for payments.
      </Text>

      {/* Icons Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: "40px",
          gap: "16px",
        }}
      >
        <Box
          sx={{
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            backgroundColor: "#F5F5F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #737373",
          }}
        >
          <IconBuildingBank size={40} color="#000" />
        </Box>
        <Box
          sx={{
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            backgroundColor: "#F5F5F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #737373",
            ml: -4,
          }}
        >
          <IconId size={40} color="#000" />
        </Box>
      </Box>

      {/* Information Sections */}
      <Box sx={{ mb: 2 }}>
        {/* Bank Details Section */}
        <Box sx={{ mb: 2 }}>
          <Text
            variant="paragraph"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              mb: "8px",
              color: "#000",
            }}
          >
            Prepare your bank details
          </Text>
          <Text
            variant="paragraph"
            component="p"
            sx={{
              fontSize: "14px",
              color: "#666",
              lineHeight: "1.5",
            }}
          >
            Payouts will be sent by bank transfer 24 hours after the game. Make
            sure to have your IBAN ready.
          </Text>
        </Box>

        {/* Identity Verification Section */}
        <Box sx={{ mb: 2 }}>
          <Text
            variant="paragraph"
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              mb: "8px",
              color: "#000",
            }}
          >
            Verify your identity as soon as possible
          </Text>
          <Text
            variant="paragraph"
            component="p"
            sx={{
              fontSize: "14px",
              color: "#666",
              lineHeight: "1.5",
            }}
          >
            Once you set up your account you can start collecting payments right
            away. We will send you a follow up email to verify your identity.
            It&apos;s important that you do this right away to avoid any delay
            with payouts on your account.
          </Text>
        </Box>
      </Box>

      {/* Terms and Conditions Checkbox */}
      <Box sx={{ display: "flex", mb: 3, alignItems: "flex-start" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={handleTermsChange}
              sx={{
                color: "#1890FF",
                "&.Mui-checked": {
                  color: "#1890FF",
                },
              }}
            />
          }
          label={
            <Text
              variant="paragraph"
              component="p"
              sx={{ fontSize: "14px", color: "#000", mt: "10px" }}
            >
              I have read and agree to the{" "}
              <Text
                component="span"
                sx={{
                  color: "#1890FF",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => {
                  // TODO: Open terms and conditions
                }}
              >
                terms and conditions
              </Text>{" "}
              of Findaside payments.
            </Text>
          }
          sx={{
            alignItems: "flex-start",
            margin: 0,
          }}
        />
      </Box>

      {/* Set Up Merchant Account Button */}
      <Button
        component={Link}
        to={getStripeOAuthUrl()}
        variant="primary"
        disabled={!agreedToTerms}
        sx={{
          width: "100%",
          fontSize: "16px",
          opacity: agreedToTerms ? 1 : 0.6,
          "&:disabled": {
            backgroundColor: "#1890FF",
            color: "white",
          },
        }}
      >
        Set up merchant account
      </Button>
    </Container>
  );
};

export default PayoutGettingStarted;
